import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Sheet, SheetContent } from './ui/sheet';
import { Send, Bot, User, Loader2, X, Sparkles, MessageCircle, Phone, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChatBot = ({ isOpen, onClose, selectedBusiness }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [hasInitialized, setHasInitialized] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize chat with greeting
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      const greeting = `Hey there! 👋 I'm Eva, your friendly AI assistant at NEURA AI SOFTWARES.

I'm here to get you connected with our amazing team - let's make it quick and painless!

**So, what's the name of your business?**`;
      
      setMessages([{
        id: 1,
        role: 'assistant',
        content: greeting,
        timestamp: new Date()
      }]);
      setHasInitialized(true);
    }
  }, [isOpen, hasInitialized]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Input validation helpers
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digit characters for counting
    const digitsOnly = phone.replace(/\D/g, '');
    // Must have at least 10 digits
    return digitsOnly.length >= 10;
  };

  const validateName = (name) => {
    // Must be at least 2 characters and only contain letters, spaces, hyphens, apostrophes
    const nameRegex = /^[a-zA-Z\s'-]{2,}$/;
    return nameRegex.test(name.trim());
  };

  // Check if the input looks like it's attempting to provide contact info
  const detectInputType = (input) => {
    const trimmed = input.trim().toLowerCase();
    
    // Check if it looks like an email (contains @)
    if (input.includes('@')) {
      return 'email';
    }
    
    // Check if it looks like a phone number (mostly digits, or starts with + or 0)
    const digitsOnly = input.replace(/\D/g, '');
    if (digitsOnly.length >= 7 || /^[\+0]/.test(input.trim())) {
      return 'phone';
    }
    
    return null;
  };

  // Validate input before sending
  const getValidationError = (input) => {
    const inputType = detectInputType(input);
    
    if (inputType === 'email' && !validateEmail(input.trim())) {
      return "That doesn't look like a valid email address. Please use the format: name@example.com";
    }
    
    if (inputType === 'phone' && !validatePhone(input.trim())) {
      return "That phone number doesn't look complete. Please include your full number with area code (at least 10 digits).";
    }
    
    return null;
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    // Validate input before sending
    const validationError = getValidationError(inputValue);
    if (validationError) {
      // Show validation error as a system message
      const errorMessage = {
        id: Date.now(),
        role: 'assistant',
        content: `⚠️ ${validationError}`,
        timestamp: new Date(),
        isValidationError: true
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage.content,
          session_id: sessionId,
          business_category: selectedBusiness?.name || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.response,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // Auto-close chat after goodbye message (conversation complete)
        if (data.response.toLowerCase().includes('goodbye')) {
          setTimeout(() => {
            onClose(false);
            // Reset for next conversation
            setMessages([]);
            setHasInitialized(false);
          }, 3000); // Close after 3 seconds so user can read the message
        }
      } else {
        throw new Error('Failed to get response');
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I apologize for the inconvenience. Please try again, or contact us directly:\n\n📧 PeterG@neura-ai.info\n📞 074 7134 3190",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Markdown-like rendering
  const renderContent = (content) => {
    const lines = content.split('\n');
    return lines.map((line, lineIndex) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      const renderedParts = parts.map((part, index) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={index} className="font-semibold">{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      return (
        <React.Fragment key={lineIndex}>
          {renderedParts}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-[420px] p-0 border-l-0 shadow-2xl flex flex-col overflow-hidden"
        data-testid="eva-chatbot"
      >
        {/* Professional Header - Blue Theme */}
        <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 text-white">
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
          
          <div className="relative p-5">
            {/* Close button */}
            <button 
              onClick={() => onClose(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Logo and Title with Air Hostess */}
            <div className="flex items-center gap-4">
              <div className="relative">
                {/* Air Hostess Image */}
                <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shadow-lg shadow-blue-700/30 overflow-hidden border-2 border-white/50">
                  <img 
                    src="/air-hostess.png" 
                    alt="Eva" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-green-400 border-3 border-blue-500 flex items-center justify-center">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold tracking-tight">Eva</h2>
                <p className="text-blue-100 text-sm font-medium">AI Booking Assistant</p>
              </div>
            </div>

            {/* Status bar */}
            <div className="mt-4 flex items-center gap-4 text-xs text-blue-100">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                Online now
              </span>
              <span className="text-blue-300">•</span>
              <span>Typically replies instantly</span>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <ScrollArea className="flex-1 bg-gradient-to-b from-blue-50 to-white">
          <div className="p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                data-testid={`chat-message-${message.role}`}
              >
                {/* Avatar */}
                <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center shadow-sm overflow-hidden ${
                  message.role === 'assistant' 
                    ? 'bg-white border-2 border-blue-300' 
                    : 'bg-gradient-to-br from-blue-500 to-blue-600'
                }`}>
                  {message.role === 'assistant' ? (
                    <img src="/air-hostess.png" alt="Eva" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-white" />
                  )}
                </div>

                {/* Message Bubble */}
                <div className={`max-w-[80%] ${message.role === 'user' ? 'text-right' : ''}`}>
                  <div className={`rounded-2xl px-4 py-3 shadow-sm ${
                    message.role === 'assistant'
                      ? 'bg-white border border-blue-200 text-gray-700 rounded-tl-md'
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-tr-md'
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">
                      {renderContent(message.content)}
                    </p>
                  </div>
                  <span className={`text-[11px] text-gray-400 mt-1.5 block ${message.role === 'user' ? 'text-right' : ''}`}>
                    {formatTime(message.timestamp)}
                  </span>
                </div>
              </div>
            ))}

            {/* Loading Indicator */}
            {isLoading && (
              <div className="flex gap-3">
                <div className="w-9 h-9 rounded-xl bg-white border-2 border-blue-300 flex items-center justify-center shadow-sm overflow-hidden">
                  <img src="/air-hostess.png" alt="Eva" className="w-full h-full object-cover" />
                </div>
                <div className="bg-white border border-blue-200 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area - Blue Theme */}
        <div className="border-t border-blue-200 bg-white p-4">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 bg-blue-50 border-blue-200 text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:ring-blue-400/20 rounded-xl h-11"
              disabled={isLoading}
              data-testid="chat-input"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              className="bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 rounded-xl h-11 w-11 p-0"
              data-testid="chat-send-btn"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          
          {/* Quick Contact Options */}
          <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-500">
            <a href="tel:07471343190" className="flex items-center gap-1.5 hover:text-blue-600 transition-colors">
              <Phone className="w-3.5 h-3.5" />
              Call Us
            </a>
            <span className="text-blue-200">|</span>
            <a href="mailto:PeterG@neura-ai.info" className="flex items-center gap-1.5 hover:text-blue-600 transition-colors">
              <Mail className="w-3.5 h-3.5" />
              Email Us
            </a>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ChatBot;
